import { statementPeriodQuery } from '../statement-period-query.js';
import { AvailableCreditResponseSchema, AgeAnalysisSchema, CashFlowSchema, CashbookPageSchema, CashFlowSeriesSchema, IndirectCashFlowSchema, IndirectCashFlowSeriesSchema, ChartOfAccountsPresetListResponseSchema, ClientStatementFeedsResponseSchema, UnpaidInvoicesStatementResponseSchema, ClientOutstandingBalanceResponseSchema, ClientTrustBalanceResponseSchema, ClientStatementSchema, ClientAccountPaymentFundingOptionsResponseSchema, ClientAccountPaymentSchema, ListClientAccountPaymentsResponseSchema, ListClientStatementsResponseSchema, SendClientStatementResponseSchema, LedgerStatusResponseSchema, LedgerSchema, LedgerWriteResponseSchema, LedgerListResponseSchema, LedgerAccountSchema, LedgerAccountListResponseSchema, SeedChartOfAccountsResponseSchema, LedgerEntrySetSchema, ReverseLedgerEntrySetResponseSchema, LedgerEntrySetListResponseSchema, BulkImportResponseSchema, TrustOpeningBalancesImportResponseSchema, TrialBalanceSchema, TrialBalanceSeriesSchema, TrustCreditorsReportResponseSchema, TrustCreditorsReportSeriesSchema, } from '@packages/types';
import { api, apiRaw, filenameFromResponse, downloadFile } from '../client.js';
export function getLedgerStatus(accountId) {
    return api(`/accounts/${accountId}/ledger-status`, LedgerStatusResponseSchema);
}
export function getClientTrustBalance(accountId, clientId) {
    return api(`/accounts/${accountId}/clients/${clientId}/trust-balance`, ClientTrustBalanceResponseSchema);
}
export function getClientOutstandingBalance(accountId, clientId) {
    return api(`/accounts/${accountId}/clients/${clientId}/outstanding-balance`, ClientOutstandingBalanceResponseSchema);
}
/**
 * The client's available credit sources for the apply-credit funding picker
 * (task #176): their open credit notes (each with remaining > 0) and their
 * document-free unallocated overpayment remainder.
 */
export function getAvailableCredit(accountId, clientId) {
    return api(`/accounts/${accountId}/clients/${clientId}/available-credit`, AvailableCreditResponseSchema);
}
/**
 * The client statement page's single read: one interleaved feed across the
 * account's active ledger kinds, plus a header per kind, a feed-wide
 * combined header, and a nullable fees-vs-funds-held position block. Always
 * requests both ledger kinds - an absent one comes back tagged
 * NOT_PROVISIONED rather than 404ing, unless every requested kind is absent.
 */
export function getClientStatementFeeds(accountId, clientId, options) {
    return api(`/accounts/${accountId}/clients/${clientId}/statement-feeds${statementPeriodQuery(options)}`, ClientStatementFeedsResponseSchema);
}
/**
 * The invoices a client still owes as at `to` (default today), with one line
 * reconciling them to the business statement's closing balance. As-at only, so
 * it takes no `from`. With `clientProjectId` it lists that matter's open
 * invoices alone, with no reconciling line.
 */
export function getClientUnpaidInvoicesStatement(accountId, clientId, options) {
    return api(`/accounts/${accountId}/clients/${clientId}/unpaid-invoices-statement${statementPeriodQuery(options)}`, UnpaidInvoicesStatementResponseSchema);
}
export function exportUnpaidInvoicesStatementPdf(accountId, clientId, options) {
    return downloadFile(`/accounts/${accountId}/clients/${clientId}/unpaid-invoices-statement.pdf${statementPeriodQuery(options)}`, 'unpaid-invoices.pdf');
}
export function exportUnpaidInvoicesStatementXlsx(accountId, clientId, options) {
    return downloadFile(`/accounts/${accountId}/clients/${clientId}/unpaid-invoices-statement.xlsx${statementPeriodQuery(options)}`, 'unpaid-invoices.xlsx');
}
export function exportClientStatementPdf(accountId, clientId, options) {
    return downloadFile(`/accounts/${accountId}/clients/${clientId}/statement.pdf${statementPeriodQuery(options)}`, 'client-statement.pdf');
}
export function exportClientStatementXlsx(accountId, clientId, options) {
    return downloadFile(`/accounts/${accountId}/clients/${clientId}/statement.xlsx${statementPeriodQuery(options)}`, 'client-statement.xlsx');
}
export function exportClientTrustStatementPdf(accountId, clientId, options) {
    return downloadFile(`/accounts/${accountId}/clients/${clientId}/trust-statement.pdf${statementPeriodQuery(options)}`, 'client-trust-statement.pdf');
}
export function exportClientTrustStatementXlsx(accountId, clientId, options) {
    return downloadFile(`/accounts/${accountId}/clients/${clientId}/trust-statement.xlsx${statementPeriodQuery(options)}`, 'client-trust-statement.xlsx');
}
export function exportCombinedClientStatementPdf(accountId, clientId, options) {
    return downloadFile(`/accounts/${accountId}/clients/${clientId}/combined-statement.pdf${statementPeriodQuery(options)}`, 'combined-client-statement.pdf');
}
export function exportCombinedClientStatementXlsx(accountId, clientId, options) {
    return downloadFile(`/accounts/${accountId}/clients/${clientId}/combined-statement.xlsx${statementPeriodQuery(options)}`, 'combined-client-statement.xlsx');
}
/**
 * Record a document-free client account credit or debit: an on-account
 * receipt, an opening balance brought forward from a previous system, or a
 * correcting adjustment. Unlike an invoice payment this settles no document -
 * it moves the client's account balance directly. See the client statement
 * page's Record-payment dialog for the three funding shapes.
 */
export function recordClientAccountPayment(accountId, clientId, input) {
    return api(`/accounts/${accountId}/clients/${clientId}/account-payments`, ClientAccountPaymentSchema, { method: 'POST', body: input });
}
export function getClientAccountPaymentFundingOptions(accountId) {
    return api(`/accounts/${accountId}/client-account-payment-funding-options`, ClientAccountPaymentFundingOptionsResponseSchema);
}
/**
 * Every document-free client account payment for this client, newest first.
 *
 * The typed client for `GET .../account-payments`. No frontend surface reads
 * it today: the client statement gets the payment behind each of its rows from
 * the statement feed itself, which does that join server-side and bounds it to
 * the period on screen rather than to the client's whole payment history.
 */
export function listClientAccountPayments(accountId, clientId) {
    return api(`/accounts/${accountId}/clients/${clientId}/account-payments`, ListClientAccountPaymentsResponseSchema);
}
/** Void a document-free client account payment. A reconciled payment must be unmatched from its bank line first. */
export function voidClientAccountPayment(accountId, clientId, paymentId) {
    return api(`/accounts/${accountId}/clients/${clientId}/account-payments/${paymentId}/void`, ClientAccountPaymentSchema, { method: 'POST', body: {} });
}
export function createClientStatement(accountId, clientId, input) {
    return api(`/accounts/${accountId}/clients/${clientId}/statements`, ClientStatementSchema, { method: 'POST', body: input });
}
export function listClientStatements(accountId, clientId) {
    return api(`/accounts/${accountId}/clients/${clientId}/statements`, ListClientStatementsResponseSchema);
}
export async function downloadClientStatementPdf(accountId, clientId, statementId) {
    const response = await apiRaw(`/accounts/${accountId}/clients/${clientId}/statements/${statementId}/pdf`);
    const filename = filenameFromResponse(response, `client-statement-${statementId}.pdf`);
    const blob = await response.blob();
    return { blob, filename };
}
export function sendClientStatement(accountId, clientId, statementId, input) {
    return api(`/accounts/${accountId}/clients/${clientId}/statements/${statementId}/send`, SendClientStatementResponseSchema, { method: 'POST', body: input });
}
export function createLedger(accountId, input) {
    return api(`/accounts/${accountId}/ledgers`, LedgerWriteResponseSchema, {
        method: 'POST',
        body: input,
    });
}
export function updateLedgerOpeningDate(accountId, ledgerId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}`, LedgerWriteResponseSchema, {
        method: 'PATCH',
        body: input,
    });
}
export const updateLedgerSettings = updateLedgerOpeningDate;
export function listLedgers(accountId) {
    return api(`/accounts/${accountId}/ledgers`, LedgerListResponseSchema);
}
export function getLedger(accountId, ledgerId) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}`, LedgerSchema);
}
/**
 * Build the querystring for a period report from a window or a preset.
 * Exported so `report-export.ts`'s download functions build the identical
 * querystring for the income-statement / cash-flow export siblings.
 */
export function periodReportQuery(range) {
    const params = new URLSearchParams();
    if (range.period !== undefined) {
        params.set('period', range.period);
    }
    else {
        params.set('from', range.from);
        params.set('to', range.to);
    }
    return params.toString();
}
/**
 * Build the querystring for an as-at report from an explicit date or a preset.
 * Exported so `report-export.ts`'s download functions build the identical
 * querystring for the balance-sheet / trial-balance / trust-creditors /
 * age-analysis export siblings.
 */
export function asOfReportQuery(at) {
    const params = new URLSearchParams();
    if (at.asOfPreset !== undefined) {
        params.set('asOfPreset', at.asOfPreset);
    }
    else {
        params.set('asOf', at.asOf);
    }
    return params.toString();
}
/**
 * The querystring every as-at series takes: an anchor plus what to compare it
 * against. Shared by the balance sheet, the trial balance and the trust
 * reconciliation, so the three cannot encode the same request differently.
 */
export function asOfSeriesQuery(at, compare) {
    const params = new URLSearchParams(asOfReportQuery(at));
    params.set('compare', compare);
    return params.toString();
}
export function reportPeriodSetQuery(input) {
    const params = new URLSearchParams();
    params.set('periodSet', input.periodSet);
    if (input.periodSet === 'rolling') {
        if (input.endingMonth !== undefined)
            params.set('endingMonth', input.endingMonth);
        if (input.monthCount !== undefined)
            params.set('monthCount', String(input.monthCount));
    }
    else {
        if (input.cutoffMonth !== undefined)
            params.set('cutoffMonth', input.cutoffMonth);
        if (input.comparatives !== undefined)
            params.set('comparatives', String(input.comparatives));
    }
    return params.toString();
}
export function createLedgerAccount(accountId, ledgerId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/accounts`, LedgerAccountSchema, {
        method: 'POST',
        body: input,
    });
}
/**
 * `includeArchived` defaults to omitted (active accounts only), matching
 * every existing caller. Pass `true` only to resolve an account regardless of
 * archive state - e.g. naming an archived account on a journal leg - never
 * for a picker or a filter dropdown.
 * `systemRole` defaults to omitted (the full chart). Pass a role to return
 * only that role's rows - e.g. `'BANK'` for an administered entity's cash
 * accounts, including archived when `includeArchived` is true.
 */
export function listLedgerAccounts(accountId, ledgerId, includeArchived, systemRole) {
    const params = new URLSearchParams();
    if (includeArchived !== undefined)
        params.set('includeArchived', String(includeArchived));
    if (systemRole !== undefined)
        params.set('systemRole', systemRole);
    const qs = params.toString();
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/accounts${qs ? `?${qs}` : ''}`, SeedChartOfAccountsResponseSchema);
}
export function patchLedgerAccount(accountId, ledgerId, ledgerAccountId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/accounts/${ledgerAccountId}`, LedgerAccountSchema, {
        method: 'PATCH',
        body: input,
    });
}
export function linkBankAccountToLedgerAccount(accountId, ledgerId, ledgerAccountId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/accounts/${ledgerAccountId}/link-bank-account`, LedgerAccountSchema, {
        method: 'POST',
        body: input,
    });
}
/**
 * Provisions a FRESH bank-role ledger account (plus its paired clearing and
 * suspense rows) for one unlinked bank account and links it - the "create a
 * new ledger account" option in `LedgerLinkPanel`. Bank-addressed (no
 * caller-chosen ledger account to name); 200 no-op returning the existing
 * bank-role row if the bank is already linked. Resource-creating POST, so
 * `input.idempotencyKey` is required - callers mint a fresh
 * `crypto.randomUUID()` per submit attempt, never a mount-scoped constant (a
 * retry after a failure is a genuinely new operation).
 */
export function createLedgerAccountForBankAccount(accountId, bankAccountId, input) {
    return api(`/accounts/${accountId}/bank-accounts/${bankAccountId}/ledger-link`, LedgerAccountSchema, { method: 'POST', body: input });
}
export function archiveLedgerAccount(accountId, ledgerId, ledgerAccountId) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/accounts/${ledgerAccountId}`, LedgerAccountSchema, { method: 'DELETE' });
}
export function createLedgerEntrySet(accountId, ledgerId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/entry-sets`, LedgerEntrySetSchema, {
        method: 'POST',
        body: input,
    });
}
export function listLedgerEntrySets(accountId, ledgerId, params) {
    const qs = new URLSearchParams();
    if (params?.cursor)
        qs.set('cursor', params.cursor);
    if (params?.limit != null)
        qs.set('limit', String(params.limit));
    for (const id of params?.ledgerAccountIds ?? [])
        qs.append('ledgerAccountIds', id);
    if (params?.from)
        qs.set('from', params.from);
    if (params?.to)
        qs.set('to', params.to);
    if (params?.period)
        qs.set('period', params.period);
    if (params?.search)
        qs.set('search', params.search);
    if (params?.excludeReversed != null)
        qs.set('excludeReversed', String(params.excludeReversed));
    if (params?.sortBy)
        qs.set('sortBy', params.sortBy);
    if (params?.sortDirection)
        qs.set('sortDirection', params.sortDirection);
    const query = qs.toString();
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/entry-sets${query ? `?${query}` : ''}`, LedgerEntrySetListResponseSchema);
}
export function getLedgerEntrySet(accountId, ledgerId, entrySetId) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/entry-sets/${entrySetId}`, LedgerEntrySetSchema);
}
export function reverseLedgerEntrySet(accountId, ledgerId, entrySetId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/entry-sets/${entrySetId}/reverse`, ReverseLedgerEntrySetResponseSchema, {
        method: 'POST',
        body: input,
    });
}
export function bulkImportLedgerAccounts(accountId, ledgerId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/accounts/bulk`, BulkImportResponseSchema, { method: 'POST', body: input });
}
/**
 * One-shot import of a trust ledger's entire opening balance sheet: one debit
 * per trust bank leg, N per-client credits to the trust creditors control
 * account, and an optional credit to the LPFF interest payable control account.
 * Each trust bank leg must already be linked to this ledger as a trust bank
 * role. Refuses with 409 if opening balances were already posted for this
 * ledger.
 */
export function postTrustOpeningBalances(accountId, ledgerId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/trust-opening-balances`, TrustOpeningBalancesImportResponseSchema, { method: 'POST', body: input });
}
export function getTrialBalance(accountId, ledgerId, at, showZeroBalances) {
    // Undefined `at` (no asOf/asOfPreset in the querystring): a ledger with a
    // financial year end returns today's balance with nominal accounts scoped to
    // the current FY; a ledger with no FYE returns the all-time balance.
    // `showZeroBalances` is tri-state, appended only when the caller passes it:
    // omitted keeps today's behaviour (active zero-balance accounts shown) -
    // the AccountsTab relies on this default and always omits it; explicit
    // `true`/`false` are sent as-is.
    const parts = [];
    if (at)
        parts.push(asOfReportQuery(at));
    if (showZeroBalances !== undefined)
        parts.push(`showZeroBalances=${showZeroBalances}`);
    const qs = parts.join('&');
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/trial-balance${qs ? `?${qs}` : ''}`, TrialBalanceSchema);
}
export function getCashFlow(accountId, ledgerId, range) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/cash-flow?${periodReportQuery(range)}`, CashFlowSchema);
}
/**
 * One page of the cashbook, named after the route and service
 * `getCashbookReport`. Each call still returns one member of `CashbookPage`.
 *
 * Parses against `CashbookPageSchema`, the union of everything this endpoint
 * can return: the full report for the first page (no cursor), movements alone
 * for every page after it. Parsing against the report schema alone type-checks
 * at every call site and still rejects the first continuation page at runtime,
 * so the schema must cover both shapes and the return type must make the
 * caller discriminate on `kind` rather than assume a report.
 */
export function getCashbookReport(accountId, ledgerId, query) {
    const qs = new URLSearchParams();
    if (query.from)
        qs.set('from', query.from);
    qs.set('to', query.to);
    qs.set('limit', String(query.limit));
    if (query.cursor)
        qs.set('cursor', query.cursor);
    if (query.bankAccountId)
        qs.set('bankAccountId', query.bankAccountId);
    if (query.ledgerAccountId)
        qs.set('ledgerAccountId', query.ledgerAccountId);
    if (query.unallocatedOnly !== undefined)
        qs.set('unallocatedOnly', String(query.unallocatedOnly));
    if (query.search)
        qs.set('search', query.search);
    if (query.sortBy)
        qs.set('sortBy', query.sortBy);
    if (query.sortDirection)
        qs.set('sortDirection', query.sortDirection);
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/cashbook?${qs.toString()}`, CashbookPageSchema);
}
export function getCashFlowSeries(accountId, ledgerId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/cash-flow-series?${reportPeriodSetQuery(input)}`, CashFlowSeriesSchema);
}
export function getIndirectCashFlowSeries(accountId, ledgerId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/indirect-cash-flow-series?${reportPeriodSetQuery(input)}`, IndirectCashFlowSeriesSchema);
}
export function getIndirectCashFlow(accountId, ledgerId, range) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/indirect-cash-flow?${periodReportQuery(range)}`, IndirectCashFlowSchema);
}
/**
 * Two-section trust creditors report: trust assets by s86(2) bank account, and
 * trust creditors by client - the Rule 54.15.1 reconciliation view.
 */
/**
 * The trial balance at several dates. `compare` is required and has no "none"
 * member: a single-column report IS `getTrialBalance`, which has its own
 * endpoint and its own renderer.
 */
export function getTrialBalanceSeries(accountId, ledgerId, at, compare, showZeroBalances) {
    const params = new URLSearchParams(asOfSeriesQuery(at, compare));
    if (showZeroBalances !== undefined)
        params.set('showZeroBalances', String(showZeroBalances));
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/trial-balance-series?${params.toString()}`, TrialBalanceSeriesSchema);
}
export function getTrustCreditors(accountId, ledgerId, at) {
    // Undefined input (no querystring): same live-default behaviour as
    // getTrialBalance - today's balance on a ledger with a financial year end,
    // all-time on a ledger without one.
    const qs = at ? asOfReportQuery(at) : '';
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/trust-creditors${qs ? `?${qs}` : ''}`, TrustCreditorsReportResponseSchema);
}
/**
 * The Rule 54.15.1 trust reconciliation at several dates. Takes no
 * `showZeroBalances`, matching `getTrustCreditors`.
 */
export function getTrustCreditorsSeries(accountId, ledgerId, at, compare) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/trust-creditors-series?${asOfSeriesQuery(at, compare)}`, TrustCreditorsReportSeriesSchema);
}
export function getDebtorsAgeAnalysis(accountId, ledgerId, at) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/debtors-age-analysis?${asOfReportQuery(at)}`, AgeAnalysisSchema);
}
export function getCreditorsAgeAnalysis(accountId, ledgerId, at) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/creditors-age-analysis?${asOfReportQuery(at)}`, AgeAnalysisSchema);
}
// ---------------------------------------------------------------------------
// Chart of accounts presets
//
// Currently gated by `requireSuperAdmin()` on the server while accounting is
// private; the URLs are deliberately not under `/admin/` so callers do not
// need to migrate when the gate is lifted.
// ---------------------------------------------------------------------------
/**
 * Lists every chart-of-accounts preset (public + admin-only). 403 for
 * non-admin callers while the feature is private.
 */
export function listChartOfAccountsPresets() {
    return api('/chart-of-accounts-presets', ChartOfAccountsPresetListResponseSchema);
}
/**
 * Seeds the supplied ledger with the chosen preset's chart of accounts.
 * Refuses with 409 if the ledger already has any accounts.
 * The ledger must already have an openingDate set (from creation).
 */
export function applyChartOfAccountsPreset(accountId, ledgerId, input) {
    return api(`/accounts/${accountId}/ledgers/${ledgerId}/chart-of-accounts-preset`, LedgerAccountListResponseSchema, {
        method: 'POST',
        body: input,
    });
}
/**
 * One-call helper: creates a ZAR ledger for the account if one does not
 * exist, then applies the chosen preset. Requires openingDate because it
 * may create the ledger on the caller's behalf.
 */
export function seedChartOfAccounts(accountId, input) {
    return api(`/accounts/${accountId}/seed-chart-of-accounts`, SeedChartOfAccountsResponseSchema, {
        method: 'POST',
        body: input,
    });
}
